import { useEffect, useRef, useState } from "react";
import { Check, X as XIcon, Loader2 } from "lucide-react";

/**
 * InlineText — click any text to edit it, blur or Enter saves, Esc cancels.
 *
 * Props:
 *   value       — current text
 *   onSave(v)   — async (or sync) handler invoked with the new value
 *   placeholder — empty-state placeholder
 *   className   — applied to both display and input forms
 *   testId      — base for data-testid on display + input + actions
 *   readOnly    — disables editing (shows plain text)
 *   as          — element to render in display mode ("span" by default)
 *   maxLength   — input maxLength
 *   ariaLabel   — accessibility label
 *
 * Behavior:
 *   - Click display → switches to input, autofocuses & selects current text.
 *   - Enter or blur → saves if changed, otherwise just exits.
 *   - Esc → cancels without saving.
 *   - Empty submit reverts to previous value (we don't blank out names).
 */
export default function InlineText({
  value,
  onSave,
  placeholder = "Click to edit",
  className = "",
  testId = "inline-text",
  readOnly = false,
  as: Tag = "span",
  maxLength = 200,
  ariaLabel = "Edit",
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const [seenValue, setSeenValue] = useState(value || "");
  if (!editing && seenValue !== (value || "")) {
    setSeenValue(value || "");
    setDraft(value || "");
  }

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editing]);

  const startEdit = (e) => {
    if (readOnly || busy) return;
    e?.stopPropagation?.();
    setDraft(value || "");
    setEditing(true);
  };

  const commit = async () => {
    const next = (draft || "").trim();
    if (!next || next === (value || "").trim()) {
      setEditing(false);
      setDraft(value || "");
      return;
    }
    setBusy(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      // Keep editing on failure so the user can retry / cancel. We log
      // so a backend regression doesn't disappear silently.
      console.warn("[inline-text] save failed, staying in edit mode:", e?.message || e);
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" data-testid={`${testId}-editing`}>
        <input
          ref={inputRef}
          className={`bd-input ${className}`}
          style={{ padding: "0.2rem 0.5rem", fontSize: "inherit", minWidth: "8ch" }}
          value={draft}
          maxLength={maxLength}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); cancel(); }
          }}
          disabled={busy}
          aria-label={ariaLabel}
          data-testid={`${testId}-input`}
        />
        {busy && <Loader2 size={12} className="animate-spin" />}
        {!busy && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={commit}
            className="bd-btn bd-btn-ghost"
            style={{ padding: "0.15rem 0.3rem" }}
            title="Save"
            data-testid={`${testId}-save`}
          >
            <Check size={12} />
          </button>
        )}
        {!busy && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancel}
            className="bd-btn bd-btn-ghost"
            style={{ padding: "0.15rem 0.3rem", color: "var(--text-dim)" }}
            title="Cancel"
            data-testid={`${testId}-cancel`}
          >
            <XIcon size={12} />
          </button>
        )}
      </span>
    );
  }

  return (
    <Tag
      role={readOnly ? undefined : "button"}
      tabIndex={readOnly ? undefined : 0}
      onClick={startEdit}
      onKeyDown={(e) => {
        if (readOnly) return;
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); startEdit(); }
      }}
      className={className}
      style={{
        cursor: readOnly ? undefined : "text",
        borderBottom: readOnly ? undefined : "1px dashed transparent",
        transition: "border-color 160ms ease",
      }}
      onMouseEnter={(e) => { if (!readOnly) e.currentTarget.style.borderBottomColor = "var(--text-dim)"; }}
      onMouseLeave={(e) => { if (!readOnly) e.currentTarget.style.borderBottomColor = "transparent"; }}
      title={readOnly ? "" : "Click to edit"}
      data-testid={testId}
    >
      {value || <span style={{ color: "var(--text-dim)", fontStyle: "italic" }}>{placeholder}</span>}
    </Tag>
  );
}
